import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { buildUrl, ENDPOINTS } from '../config';
import useFieldConfig from '../hooks/useFieldConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import './ElevatorDetail.css';

function ElevatorDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fields, loading: fieldsLoading } = useFieldConfig();
    const [elevator, setElevator] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadElevator = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get(buildUrl(ENDPOINTS.elevator(id)));
                setElevator(response.data);
            } catch (requestError) {
                console.error('加载电梯信息失败:', requestError);
                setError('未找到对应的电梯档案。');
            } finally {
                setLoading(false);
            }
        };

        if (id && !fieldsLoading) {
            loadElevator();
        }
    }, [fieldsLoading, id]);

    const detailFields = useMemo(
        () => fields.filter((field) => field.name !== 'id'),
        [fields]
    );

    const filledFieldCount = useMemo(() => {
        if (!elevator) {
            return 0;
        }

        return detailFields.filter((field) => elevator[field.name]).length;
    }, [detailFields, elevator]);

    if (fieldsLoading || loading) {
        return <LoadingSpinner message="正在加载电梯详情..." />;
    }

    if (error) {
        return (
            <div className="page-shell">
                <div className="page-container detail-page">
                    <div className="app-panel app-message-card detail-message-card">
                        <span className="app-eyebrow">查询结果</span>
                        <h2>{error}</h2>
                        <p>请确认电梯编号是否正确，或返回首页重新查询。</p>
                        <div className="detail-message-card__actions">
                            <button type="button" className="app-button app-button--primary" onClick={() => navigate('/')}>
                                返回首页
                            </button>
                            <button type="button" className="app-button app-button--secondary" onClick={() => navigate('/admin')}>
                                进入管理端
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!elevator) {
        return null;
    }

    return (
        <div className="page-shell">
            <div className="page-container detail-page">
                <div className="detail-toolbar">
                    <button type="button" className="app-button app-button--secondary" onClick={() => navigate('/')}>
                        返回首页
                    </button>
                    <span className="app-badge">档案详情页</span>
                </div>

                <section className="detail-summary app-panel">
                    <div className="detail-summary__content">
                        <span className="app-eyebrow">电梯档案</span>
                        <h1>{elevator.id}</h1>
                        <p>可通过二维码或直链访问此页面，查看该电梯当前登记信息。</p>
                    </div>

                    <div className="app-kpi-grid detail-summary__stats">
                        <div className="app-kpi-card">
                            <strong>{filledFieldCount}</strong>
                            <span>已填写字段</span>
                        </div>
                        <div className="app-kpi-card">
                            <strong>{detailFields.length}</strong>
                            <span>总展示字段</span>
                        </div>
                    </div>
                </section>

                <section className="detail-content app-panel app-panel--strong">
                    <div className="section-heading detail-content__heading">
                        <div>
                            <h2>登记信息</h2>
                            <p>以下字段按系统配置展示，未填写的字段会直接标记出来，方便快速确认。</p>
                        </div>
                        <span className="app-badge app-badge--accent">
                            更新时间：{new Date().toLocaleString('zh-CN')}
                        </span>
                    </div>

                    <div className="detail-grid">
                        {detailFields.map((field) => (
                            <div key={field.name} className="detail-field">
                                <div className="detail-field__label">{field.label}</div>
                                <div className={elevator[field.name] ? 'detail-field__value' : 'detail-field__value detail-field__empty'}>
                                    {elevator[field.name] || '未填写'}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}

export default ElevatorDetail;
